/**
 * iOS/iPadOS Home Screen apps use storage isolated from Safari tabs.
 * Only HTTP cookies are copied once at “Add to Home Screen” (iOS 17.2+).
 * Küchen-Bind must live in cookies + optional ?bind= on start_url.
 */

const COOKIE_BIND = 'ollaa_kitchen_bind';
const COOKIE_SETUP = 'ollaa_kitchen_setup';
const COOKIE_PWA_MANIFEST = 'ollaa_kitchen_pwa_manifest';
const COOKIE_TABLET_INTENT = 'ollaa_kitchen_tablet_intent';

const MAX_AGE_SEC = 63072000; // ~2 years

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const readKitchenCookie = (name) => {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${escapeRegExp(name)}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : '';
};

const cookieSecureSuffix = () => (
  typeof window !== 'undefined' && window.location?.protocol === 'https:'
    ? '; secure'
    : ''
);

export const writeKitchenCookie = (name, value) => {
  if (typeof document === 'undefined') return;
  const encoded = encodeURIComponent(String(value));
  document.cookie = `${name}=${encoded}; path=/; max-age=${MAX_AGE_SEC}; samesite=lax${cookieSecureSuffix()}`;
};

export const clearKitchenCookie = (name) => {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; path=/; max-age=0; samesite=lax${cookieSecureSuffix()}`;
};

export const syncKitchenBindCookie = (bindToken) => {
  const value = String(bindToken || '').trim();
  if (!value) {
    clearKitchenCookie(COOKIE_BIND);
    clearKitchenCookie(COOKIE_TABLET_INTENT);
    return;
  }
  writeKitchenCookie(COOKIE_BIND, value);
  writeKitchenCookie(COOKIE_TABLET_INTENT, '1');
};

export const syncKitchenSetupCookie = (complete) => {
  if (complete) writeKitchenCookie(COOKIE_SETUP, '1');
  else clearKitchenCookie(COOKIE_SETUP);
};

export const syncKitchenPwaManifestCookie = (active) => {
  if (active) writeKitchenCookie(COOKIE_PWA_MANIFEST, '1');
  else clearKitchenCookie(COOKIE_PWA_MANIFEST);
};

export const clearAllKitchenBindCookies = () => {
  clearKitchenCookie(COOKIE_BIND);
  clearKitchenCookie(COOKIE_SETUP);
  clearKitchenCookie(COOKIE_PWA_MANIFEST);
  clearKitchenCookie(COOKIE_TABLET_INTENT);
};

/** Copy cookies (and optional ?bind=) into localStorage for this WebKit container. */
export const hydrateKitchenStorageFromCookies = () => {
  if (typeof window === 'undefined') return;

  try {
    const params = new URLSearchParams(window.location.search || '');
    const bindFromUrl = String(params.get('bind') || '').trim();
    if (bindFromUrl) {
      window.localStorage.setItem('ollaa_kitchen_bind_token', bindFromUrl);
      window.localStorage.setItem('ollaa_kitchen_tablet_intent', '1');
      syncKitchenBindCookie(bindFromUrl);
    }

    const bindFromCookie = readKitchenCookie(COOKIE_BIND).trim();
    if (bindFromCookie && !window.localStorage.getItem('ollaa_kitchen_bind_token')) {
      window.localStorage.setItem('ollaa_kitchen_bind_token', bindFromCookie);
      window.localStorage.setItem('ollaa_kitchen_tablet_intent', '1');
    }

    if (readKitchenCookie(COOKIE_SETUP) === '1') {
      window.localStorage.setItem('ollaa_kitchen_setup_complete', '1');
    }
    if (readKitchenCookie(COOKIE_PWA_MANIFEST) === '1') {
      window.localStorage.setItem('ollaa_kitchen_pwa_manifest', '1');
    }
    if (readKitchenCookie(COOKIE_TABLET_INTENT) === '1') {
      window.localStorage.setItem('ollaa_kitchen_tablet_intent', '1');
    }
  } catch {
    // private mode / storage blocked
  }
};
